export const AUTH_SESSION_EXPIRED_EVENT = 'auth:session-expired';
export const TENANT_SESSION_UPDATED_EVENT = 'tenant:session-updated';

const USER_KEY = 'user';
const AUTH_PERSIST_KEY = 'auth_persist_login';
const REMEMBERED_LOGIN_KEY = 'remembered_login';
const TENANT_USER_KEY = 'tenant_user';

const getStorage = (persistLogin: boolean) => (persistLogin ? localStorage : sessionStorage);

const readValue = (key: string): string | null => {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
};

const removeValue = (key: string) => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

const getTenantSessionStorage = () => sessionStorage;

const removeTenantValue = (key: string) => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

export interface StoredUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  isSuperAdmin: boolean;
}

export interface StoredAuthSession {
  user: StoredUser | null;
  persistLogin: boolean;
}

export interface RememberedLogin {
  username: string;
  keepLogin30Days: boolean;
}

export interface TenantStoredUser {
  id: string;
  username: string;
  email: string;
  fullName: string;
  avatarUrl?: string;
  tenantId: string;
  isTenantAdmin: boolean;
  mustChangePassword?: boolean;
  roleId?: number | null;
  roleCode?: string;
  roleName?: string;
  permissions?: string[];
  allowedPaths?: string[];
  assignedPlanCode?: string | null;
  assignedPlanName?: string | null;
}

export interface AuthResponseLike {
  accessToken?: string;
  refreshToken?: string;
  user: StoredUser;
}

export interface AuthSessionExpiredDetail {
  tenant: boolean;
}

export const dispatchAuthSessionExpired = (detail: AuthSessionExpiredDetail) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<AuthSessionExpiredDetail>(AUTH_SESSION_EXPIRED_EVENT, {
      detail,
    }),
  );
};

export const dispatchTenantSessionUpdated = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(TENANT_SESSION_UPDATED_EVENT));
};

export const getPersistLoginPreference = (): boolean => localStorage.getItem(AUTH_PERSIST_KEY) === '1';

export const getStoredAuthSession = (): StoredAuthSession | null => {
  const userStr = readValue(USER_KEY);

  if (!userStr) {
    return null;
  }

  let user: StoredUser | null = null;
  try {
    user = JSON.parse(userStr) as StoredUser;
  } catch {
    user = null;
  }

  return {
    user,
    persistLogin: getPersistLoginPreference(),
  };
};

export const saveAuthSession = (
  auth: AuthResponseLike,
  options?: { persistLogin?: boolean },
) => {
  const persistLogin = options?.persistLogin ?? getPersistLoginPreference();

  clearAuthSession();

  const storage = getStorage(persistLogin);
  storage.setItem(USER_KEY, JSON.stringify(auth.user));

  if (persistLogin) {
    localStorage.setItem(AUTH_PERSIST_KEY, '1');
  } else {
    localStorage.removeItem(AUTH_PERSIST_KEY);
  }
};

export const clearAuthSession = () => {
  removeValue('access_token');
  removeValue('refresh_token');
  removeValue(USER_KEY);
  localStorage.removeItem(AUTH_PERSIST_KEY);
};

export const getAccessToken = (): string | null => {
  return null;
};

export const getRefreshToken = (): string | null => {
  return null;
};

export const getRememberedLogin = (): RememberedLogin | null => {
  const raw = localStorage.getItem(REMEMBERED_LOGIN_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as RememberedLogin;
  } catch {
    localStorage.removeItem(REMEMBERED_LOGIN_KEY);
    return null;
  }
};

export const saveRememberedLogin = (login: RememberedLogin) => {
  localStorage.setItem(
    REMEMBERED_LOGIN_KEY,
    JSON.stringify({
      username: login.username,
      keepLogin30Days: !!login.keepLogin30Days,
    }),
  );
};

export const clearRememberedLogin = () => {
  localStorage.removeItem(REMEMBERED_LOGIN_KEY);
};

export const saveTenantSession = (_accessToken?: string | null, user?: TenantStoredUser | null) => {
  removeTenantValue('tenant_access_token');
  removeTenantValue(TENANT_USER_KEY);
  const storage = getTenantSessionStorage();
  if (user) {
    storage.setItem(TENANT_USER_KEY, JSON.stringify(user));
  }
  dispatchTenantSessionUpdated();
};

export const getTenantStoredUser = (): TenantStoredUser | null => {
  const raw = sessionStorage.getItem(TENANT_USER_KEY) ?? localStorage.getItem(TENANT_USER_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as TenantStoredUser;
  } catch {
    removeTenantValue(TENANT_USER_KEY);
    return null;
  }
};

export const updateTenantStoredUser = (patch: Partial<TenantStoredUser>) => {
  const current = getTenantStoredUser();
  if (!current) {
    return;
  }

  getTenantSessionStorage().setItem(
    TENANT_USER_KEY,
    JSON.stringify({ ...current, ...patch }),
  );
  dispatchTenantSessionUpdated();
};

export const clearTenantSession = () => {
  removeTenantValue('tenant_access_token');
  removeTenantValue(TENANT_USER_KEY);
  dispatchTenantSessionUpdated();
};

const OPERATOR_ALLOWED_PATHS = [
  '/tenant/dashboard',
  '/tenant/short-links',
  '/tenant/domains',
  '/tenant/plan',
  '/tenant/profile',
];

const DEFAULT_TENANT_LANDING_PATHS = [
  '/tenant/dashboard',
  '/tenant/short-links',
  '/tenant/website-config',
  '/tenant/seo-config',
  '/tenant/domains',
  '/tenant/users',
  '/tenant/roles',
  '/tenant/plan',
  '/tenant/wechat-pay-config',
  '/tenant/alipay-config',
  '/tenant/profile',
];

export const isTenantOperatorRole = (user?: TenantStoredUser | null) => {
  return Boolean(user?.roleCode?.endsWith('-operator'));
};

export const canAccessTenantPath = (user: TenantStoredUser | null, pathname: string) => {
  if (!user) {
    return false;
  }

  if (user.isTenantAdmin) {
    return true;
  }

  if (Array.isArray(user.allowedPaths)) {
    return user.allowedPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  }

  if (isTenantOperatorRole(user)) {
    return OPERATOR_ALLOWED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  }

  return OPERATOR_ALLOWED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
};

export const filterTenantMenuItems = <T extends { key: string }>(items: T[], user: TenantStoredUser | null) => {
  if (!user) {
    return items;
  }

  if (user.isTenantAdmin) {
    return items;
  }

  if (Array.isArray(user.allowedPaths)) {
    const allowedPaths = user.allowedPaths;
    return items.filter((item) =>
      allowedPaths.some((path) => item.key === path || item.key.startsWith(`${path}/`) || path.startsWith(`${item.key}/`)));
  }

  if (isTenantOperatorRole(user)) {
    return items.filter((item) => canAccessTenantPath(user, item.key));
  }

  return items.filter((item) => canAccessTenantPath(user, item.key));
};

export const getTenantLandingPath = (user: TenantStoredUser | null) => {
  if (!user) {
    return '/tenant/login';
  }

  if (Array.isArray(user.allowedPaths)) {
    const normalizedAllowed = user.allowedPaths.filter(Boolean);
    const preferred = DEFAULT_TENANT_LANDING_PATHS.find((path) => normalizedAllowed.includes(path));
    return preferred || normalizedAllowed[0] || '/tenant/login';
  }

  if (user.isTenantAdmin || isTenantOperatorRole(user)) {
    return '/tenant/dashboard';
  }

  return '/tenant/profile';
};
